import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import Button from 'components/Button';
import TextField from 'components/TextField';
import Typography from 'components/Typography';
import CheckboxField from 'components/CheckboxField';

import useAuth from 'hooks/useAuth';

import styles from './Login.module.scss';
import { validateEmail } from 'utils/validators';
import EyeIcon from '../EmployeeForm/icons/eye.svg';
import EyeDisabledIcon from '../EmployeeForm/icons/eye-disable.svg';
import { get } from 'lodash';
import Layout from 'components/Layout';
import backgroundImage from 'assets/banner.avif';

interface FormValue {
  email: string;
  password: string;
}

function Login() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValue>();
  const { login } = useAuth();

  async function submit(data: FormValue) {
    setLoading(true);

    try {
      await login(data.email, data.password);
    } catch (err) {
      let error_message = '';
      const error = get(err, 'response.data', {});
      Object.keys(error).forEach((item: any) => {
        const message = error[item as keyof typeof error];

        error_message = typeof message === 'string' ? message : message[0];
      });

      setError('email', { type: 'manual', message: ' ' });
      setError('password', {
        type: 'manual',
        message: error_message || 'Something went wrong',
      });
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <div className={styles.root}>
        <div className={styles.left}>
          <Typography variant="h1" textAlign="center" className={styles.title}>
            Welcome to Magic Dispatch
          </Typography>
          <Typography
            variant="p"
            textAlign="center"
            className={styles.description}
          >
            Smarter way to dispatch jobs and deliveries
          </Typography>
          <form onSubmit={handleSubmit(submit)}>
            <TextField
              type="email"
              label="Email"
              placeholder="Email"
              {...register('email', { validate: validateEmail })}
              error={errors.email?.message}
            />
            <div style={{ position: 'relative' }}>
              <TextField
                type={showPassword ? 'text' : 'password'}
                label="Password"
                placeholder="Password"
                {...register('password', { required: 'Required' })}
                error={errors.password?.message}
              />
              <img
                onClick={() => setShowPassword((pr) => !pr)}
                className={styles.eyeIcon}
                src={showPassword ? EyeIcon : EyeDisabledIcon}
                alt="eye"
              />
            </div>
            <div className={styles.remember}>
              <CheckboxField defaultChecked label="Remember me" />
              <Link className={styles.link} to="/forgot_password">
                Forgot password?
              </Link>
            </div>
            <div className={styles.button_container}>
              <Button
                type="submit"
                loading={loading}
                className={styles.loginButton}
              >
                Login
              </Button>
            </div>
          </form>
          <div className={styles.signup}>
            <Typography variant="p">
              Don’t have an account?{' '}
              <Link className={styles.link} to="/signup">
                Sign Up
              </Link>
            </Typography>
          </div>
        </div>
        <div className={styles.right}>
          <img
            src={backgroundImage}
            alt="login"
            className={styles.backgroundImage}
          />
          <div className={styles.backgroundImageOverlay}>
            <Typography
              variant="h2"
              // textAlign="center"
              className={styles.backgroundImageTitle}
            >
              One Platform
              <br /> Endless Possibilities
            </Typography>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Login;
